import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subscription, tap, timer } from 'rxjs';
import { environment } from '../../environments/environment';

export type CategoryRequestStatus = 'Pending' | 'Created' | 'Mapped' | 'Rejected';

/** A seller's request for a category the central taxonomy lacks (SubscriptionManagement CategoryRequest). */
export interface CategoryRequest {
  categoryRequestId: string;
  businessId: string;
  businessName: string | null;
  name: string;
  parentCategoryId: string | null;
  parentCategoryName: string | null;
  exampleItemName: string | null;
  note: string | null;
  status: CategoryRequestStatus;
  resolvedCategoryId: string | null;
  resolvedCategoryName: string | null;
  submittedBy: string | null;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

/** SubscriptionManagement PagedResult<T>. */
export interface CategoryRequestPage {
  items: CategoryRequest[];
  total: number;
  page: number;
  pageSize: number;
}

/** The category staff create for a request (SubscriptionManagement CategoryInputModel). */
export interface NewCategoryInput {
  categoryName: string;
  categoryDesc: string;
  categoryImg: string;
  isActive: boolean;
  parentId: string | null;
  isEbt: boolean;
  taxRate: number | null;
  slug: string;
  level: number;
  isFeatured: boolean;
  isDeleted: boolean;
  inSeason: boolean;
}

/**
 * Seller category requests (product brand spec section 7.2), backed by SubscriptionManagement
 * api/admin/category-requests. Sellers can't create categories; they ask, and staff create the
 * category, map the request to an existing one, or decline it. The seller is told either way.
 *
 * Also owns the sidebar badge: requests waiting, polled once a minute while an admin is signed in.
 */
@Injectable({ providedIn: 'root' })
export class CategoryRequestsAdminService {
  static readonly PAGE_SIZE = 25;
  private static readonly POLL_MS = 60_000;

  private readonly baseUrl = environment.subscriptionApiUrl + '/api/admin/category-requests';

  /** Requests waiting for a decision, for the sidebar badge. */
  readonly pendingCount = signal(0);
  private polling?: Subscription;

  constructor(private http: HttpClient) {}

  getRequests(status: CategoryRequestStatus | 'All', pageNumber: number): Observable<CategoryRequestPage> {
    const params = new HttpParams()
      .set('status', status)
      .set('pageNumber', pageNumber)
      .set('pageSize', CategoryRequestsAdminService.PAGE_SIZE);
    return this.http.get<CategoryRequestPage>(this.baseUrl, { params });
  }

  createCategory(id: string, category: NewCategoryInput, note?: string): Observable<CategoryRequest> {
    return this.http.post<CategoryRequest>(`${this.baseUrl}/${encodeURIComponent(id)}/create`, { category, note: note || null })
      .pipe(tap(() => this.refreshCount()));
  }

  map(id: string, categoryId: string, note?: string): Observable<CategoryRequest> {
    return this.http.post<CategoryRequest>(`${this.baseUrl}/${encodeURIComponent(id)}/map`, { categoryId, note: note || null })
      .pipe(tap(() => this.refreshCount()));
  }

  reject(id: string, note: string): Observable<CategoryRequest> {
    return this.http.post<CategoryRequest>(`${this.baseUrl}/${encodeURIComponent(id)}/reject`, { note })
      .pipe(tap(() => this.refreshCount()));
  }

  /** Starts the badge poll (idempotent). */
  startPolling(): void {
    if (this.polling) return;
    this.polling = timer(0, CategoryRequestsAdminService.POLL_MS).subscribe(() => this.refreshCount());
  }

  stopPolling(): void {
    this.polling?.unsubscribe();
    this.polling = undefined;
    this.pendingCount.set(0);
  }

  refreshCount(): void {
    this.http.get<{ count: number }>(`${this.baseUrl}/count`).subscribe({
      next: r => this.pendingCount.set(r?.count ?? 0),
      // A badge is not worth an error on screen; the next poll tries again.
      error: () => {}
    });
  }
}
